import { Modifier } from '@dnd-kit/core'
import { useSelector } from 'react-redux'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { useTimelineMagnetize } from 'hooks/use-timeline-magnetize'
import { generateBreakpoints } from 'store/features/projectData/utils'
import { editorSelectors } from 'store/features/editor/selectors'
import { useScrollDelta } from 'hooks/useScrollDelta'
import { useTimelineContext } from 'context/TimelineContext'
import { getIsOverlapDuringDrag } from './utils'
import { IDndItem } from './types'

export const useCreateSnapModifier = (items: IDndItem[]) => {
  const timelineItemsSortedByStartTime = useSelector(
    projectDataSelectors.timelineItemsSortedByStartTime
  )
  const timelineRulerSliceDuration = useSelector(
    editorSelectors.timelineRulerSliceDuration
  )
  const { magnetizeDrag, hideMagnetizingLine } = useTimelineMagnetize()
  const { scrollableContainerRef } = useTimelineContext()

  const { getScrollDelta, onDragStart } = useScrollDelta(scrollableContainerRef)

  const createSnapModifier = (event: Parameters<Modifier>[0]) => {
    const { transform, active, over } = event

    const currentItem = active?.data.current as IDndItem

    const itemsWithoutCurrentItem = timelineItemsSortedByStartTime.filter(
      item => item.id !== currentItem?.id
    )

    const breakpoints = generateBreakpoints(
      itemsWithoutCurrentItem,
      timelineRulerSliceDuration
    )

    if (!currentItem || !over) return transform

    // Adjust transform.x for scroll delta ONLY for magnetizing calculations
    const scrollDelta = getScrollDelta()
    const adjustedTransformX = transform.x + scrollDelta

    const { id: lineIndex } = over as {
      id: number
    }

    const isOverlap = getIsOverlapDuringDrag(
      currentItem,
      items,
      lineIndex,
      adjustedTransformX
    )
    if (isOverlap) {
      hideMagnetizingLine()
      return transform
    }

    const magnetizedDeltaX = magnetizeDrag(
      adjustedTransformX,
      currentItem.left,
      currentItem.left + currentItem.width,
      breakpoints
    )

    // Subtract scroll delta back from the final result to prevent double compensation
    const finalDeltaX = magnetizedDeltaX - scrollDelta

    return {
      ...transform,
      x: finalDeltaX
    }
  }

  return { createSnapModifier, onDragStart, hideMagnetizingLine }
}
